import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DecoratedEvent } from '@/features/events/types';
import {
  getIntegrationExportPlatform,
  hasAutoUpdateExport,
} from '@/features/interactions/lib/exportStatus.lib';
import type { PlatformTypes } from '@/types/gql.generated';
import { getName } from '@/utils/platformLib';

type State = {
  hasShown: boolean;
  pendingForEventId: string | null;
};

type Actions = {
  trigger: (eventId: string) => void;
  dismiss: () => void;
};

const useStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      hasShown: false,
      pendingForEventId: null,
      trigger: (eventId) => {
        if (get().hasShown) return;
        set({ pendingForEventId: eventId });
      },
      dismiss: () => set({ hasShown: true, pendingForEventId: null }),
    }),
    {
      name: '_ah_auto_sync_tip',
      partialize: (state) => ({ hasShown: state.hasShown }),
    }
  )
);

export const useTriggerAutoSyncTip = () => useStore((s) => s.trigger);

type TipProps = { forceTooltip: true; tooltip: string };

type UseAutoSyncTipPropsParams = {
  entry: DecoratedEvent;
  platform: PlatformTypes | null;
};

export const useAutoSyncTipProps = ({
  entry,
  platform,
}: UseAutoSyncTipPropsParams): TipProps | Record<string, never> => {
  const pendingForEventId = useStore((s) => s.pendingForEventId);
  const dismiss = useStore((s) => s.dismiss);
  const [snapshot, setSnapshot] = useState<TipProps | null>(null);

  // Any integration destination qualifies: the tip explains that an
  // auto-updating export row carries edits over, which is provider-agnostic —
  // the control's platform just has to be the provider whose row auto-updates.
  const exportPlatform = platform
    ? getIntegrationExportPlatform(platform)
    : undefined;
  const isMatch =
    pendingForEventId === entry.id &&
    !!exportPlatform &&
    hasAutoUpdateExport(entry, exportPlatform);

  useEffect(() => {
    if (isMatch && !snapshot && platform) {
      setSnapshot({
        forceTooltip: true,
        tooltip: `Changes are sent to your ${getName(platform)} calendar automatically (within a few minutes)`,
      });
      dismiss();
    }
  }, [isMatch, snapshot, platform, dismiss]);

  return snapshot ?? {};
};
